import { mapActionToFormValues } from 'src/graphql/entities/behavior';
import { ActionType } from 'src/graphql/entities/behavior/behavior-config/behavior-config.types';
import type { Macro } from 'src/graphql/entities/macro/macro';
import { resolveMacroSnippetSource } from 'src/graphql/entities/macro/macro.mappings';
import {
  MacroActionType,
  MacroFolderOption,
  MacroMessagePrivacy,
  MacrosFormFields,
  MacrosFormModel,
  MacroVisibility,
} from 'src/graphql/entities/macro/macro.types';
import type { RecordFolder } from 'src/graphql/entities/record-folder/record-folder.types';
import type { ConfigActionFields } from 'src/pages/Settings/rules/use-rules-engine';
import type { MacrosEngineConfig } from './use-macros-engine';

function resolveMacroVisibility(macro: Macro): MacroVisibility {
  if (!macro.public) {
    return MacroVisibility.OnlyMe;
  }

  if (macro.conversationAssigneeGroups.length > 0) {
    return MacroVisibility.SpecificGroups;
  }

  return MacroVisibility.Everyone;
}

export function mapRecordFoldersToOptions(
  recordFolders: RecordFolder[],
  folderPartnerId: number,
): MacroFolderOption[] {
  return recordFolders
    .filter((folder) => folder.partnerId === folderPartnerId)
    .map((folder) => ({
      id: folder.id,
      label: folder.name,
      value: folder.id,
      partnerId: folder.partnerId,
    }));
}

export function mapMacroToFormValues(
  macro: Macro,
  config: MacrosEngineConfig,
): MacrosFormModel {
  const configActions: ConfigActionFields = {
    [ActionType.Data]: config.actions[ActionType.Data] ?? [],
    [ActionType.SendSlackMessage]: [],
    [ActionType.SendConversationMessage]: [],
    [ActionType.Webhook]: [],
    [ActionType.ManageConversationLabels]:
      config.actions[ActionType.ManageConversationLabels] ?? [],
    [ActionType.ManageSubAssignees]: config.actions[ActionType.ManageSubAssignees] ?? [],
    [ActionType.Signal]: config.actions[ActionType.Signal] ?? [],
    [ActionType.CreateTwilioTask]: [],
    [ActionType.RunTriage]: [],
  };
  const mappedActions = macro.actions
    .filter((action) => {
      const actionType = action.type as MacroActionType;
      return Boolean(config.actions[actionType]);
    })
    .map((action) =>
      mapActionToFormValues(action, configActions, config.queryOptions),
    ) as MacrosFormModel[MacrosFormFields.Actions];

  const messagePrivacy =
    (macro.messagePrivacy as MacroMessagePrivacy) === MacroMessagePrivacy.Private
      ? MacroMessagePrivacy.Private
      : MacroMessagePrivacy.Public;

  return {
    [MacrosFormFields.Name]: macro.name,
    [MacrosFormFields.Description]: macro.description ?? '',
    [MacrosFormFields.Visibility]: resolveMacroVisibility(macro),
    [MacrosFormFields.AvailableFor]: macro.conversationAssigneeGroups,
    [MacrosFormFields.MessagePrivacy]: messagePrivacy,
    [MacrosFormFields.SnippetSource]: resolveMacroSnippetSource(macro),
    [MacrosFormFields.Template]: macro.template ?? '',
    [MacrosFormFields.AiPromptId]: macro.aiPromptId ?? null,
    [MacrosFormFields.AiPromptText]: macro.aiPromptText ?? '',
    [MacrosFormFields.Actions]: mappedActions,
    [MacrosFormFields.RecordFolderId]: null,
  };
}
